import { createApp } from 'vue';
import App from './App.vue';
import router from './router';

import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import { definePreset } from '@primevue/themes';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';

import './assets/tailwind.css';
import store from './store'; // Vuex store
import jsonPtBr from './assets/primevue_pt-BR.json';

import Compressor from 'compressorjs';
import ImageUploadService from '@/services/imageUploadService';
import Tooltip from 'primevue/tooltip';

const CustomPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#f0f9eb',
            100: '#d5f0c8',
            200: '#bae7a5',
            300: '#9fdd82',
            400: '#84d45f',
            500: '#382e27',  // Base color
            600: '#050c03',  // #2b6216
            700: '#204911',
            800: '#15310b',
            900: '#0a1805',
            950: '#050c03'
        }
    }
});

// Fix for passive event listener warnings
if (typeof EventTarget !== 'undefined') {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function(type, listener, options) {
        if (type === 'wheel' || type === 'mousewheel' || type === 'touchstart' || type === 'touchmove') {
            if (typeof options === 'boolean') {
                options = { capture: options, passive: true };
            } else if (typeof options === 'object' && options.passive === undefined) {
                options.passive = true;
            } else if (options === undefined) {
                options = { passive: true };
            }
        }
        return originalAddEventListener.call(this, type, listener, options);
    };
}

createApp(App)
    .directive('tooltip', Tooltip)
    .use(store)
    .use(router)
    .use(ToastService)
    .use(ConfirmationService)
    .use(PrimeVue, {
        theme: {
            preset: CustomPreset,
            options: {
                darkModeSelector: 'light',
            }
        },
        locale: jsonPtBr
    })
    .mixin({
        methods: {
            makeRedirect(path) {
                this.$router.push(path);
            },
            sendToast(summary, detail, severity, life = 6500) {
                this.$toast.add({ severity, summary, detail, life });
            },
            capitalize(s) {
                return s && String(s[0]).toUpperCase() + String(s).slice(1);
            },
            formatCurrency(number) {
                return number.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });
            },
            isUserValidCpf(cpf) {
                cpf = cpf.replace(/[^\d]+/g, '');
                if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

                let sum = 0;
                let remainder;

                for (let i = 1; i <= 9; i++) {
                    sum += parseInt(cpf.charAt(i - 1)) * (11 - i);
                }

                remainder = (sum * 10) % 11;
                if (remainder === 10 || remainder === 11) remainder = 0;
                if (remainder !== parseInt(cpf.charAt(9))) return false;

                sum = 0;
                for (let i = 1; i <= 10; i++) {
                    sum += parseInt(cpf.charAt(i - 1)) * (12 - i);
                }

                remainder = (sum * 10) % 11;
                if (remainder === 10 || remainder === 11) remainder = 0;

                return remainder === parseInt(cpf.charAt(10));
            },
            getImageUrl(path) {
                return process.env.VUE_APP_URL + path;
            },
            async generateThumbnail(imageUrl) {
                try {
                    const response = await fetch(imageUrl);
                    if (!response.ok) throw new Error('Error fetching image');

                    const blob = await response.blob();
                    const file = new File([blob], 'image.jpg', { type: blob.type });

                    const compressed = await new Promise((resolve, reject) => {
                        new Compressor(file, {
                            quality: 0.6,
                            maxWidth: 80,
                            maxHeight: 60,
                            success(result) {
                                resolve(result);
                            },
                            error(err) {
                                reject(err);
                            },
                        });
                    });

                    const thumbUrl = URL.createObjectURL(compressed);
                    return thumbUrl;

                } catch (e) {
                    console.error('(main.js - generateThumbnail) Error:', e.message);
                    return null;
                }
            },
            async sendMultipleImageRequest(images, uploadType) {
                const formData = new FormData();
                formData.append('uploadType', uploadType);

                for (const image of images) {
                    formData.append('images', image);
                }

                try {
                    return await ImageUploadService.uploadImage(formData);
                } catch (error) {
                    console.error('(RoomCreateView) Failed to send image to back-end', error);
                    // this.sendToast("Falha de processamento", "Ocorreu uma falha de processamento interna. Tente atualizar a página ou contate o suporte.", "error");
                }
            },
        }
    })
    .mount('#app');
